import React, { useState, useRef, useEffect } from 'react';
import './TemplateGallery.css';
import defaultTemplateImage from '../../../../assets/template-default-image.png';
import { MindMapContext } from '../../../../Context/contex';
import TemplateCardMenu from './TemplateCardMenu';
import { TEMPLATE_CATEGORIES } from '../..';

const TemplateCard = ({ template, onOpenTemplate, onDeleted }) => {
  const { thumbnail, name, theme, _id } = template;
  const { admins = [], userInfo } = MindMapContext();
  const isAdmin = Array.isArray(admins) && admins.some(a => (a.adminEmail || a.email) === userInfo?.email);
  const title = name || 'Template';
  const subtitle = theme || '';
  const usageCount = template?.usageCount ?? template?.usedCount ?? template?.uses;
  const usageText = usageCount ? `Used ${usageCount} times` : 'Used 12k times';

  return (
    <div className="tg-card" role="presentation">
      <div className="tg-card-surface">
        <div className="tg-thumbnail">
          <img
            src={thumbnail || defaultTemplateImage}
            alt={title}
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = defaultTemplateImage; }}
            className="tg-thumbnail-img"
          />
          <div className="tg-thumbnail-overlay">
            <button
              className="tg-quick-create-btn"
              onClick={(e) => {
                e.stopPropagation();
                onOpenTemplate(template);
              }}
            >
              Quick Create
            </button>
          </div>
        </div>

        <div className="tg-card-content">
          <div className="tg-card-text">
            <div className="tg-card-title" title={title}>
              {title}
            </div>
            {subtitle ? <div className="tg-card-subtitle">{subtitle}</div> : null}
          </div>

          <div className="tg-card-footer">
            <div className="tg-card-usage" aria-label={usageText}>
              <svg className="tg-card-usage-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <span className="tg-card-usage-text">{usageText}</span>
            </div>
            {isAdmin && (
              <div className="tg-card-menu" onClick={(e) => e.stopPropagation()}>
                <TemplateCardMenu templateId={_id} onDeleted={onDeleted} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const TemplateGallery = ({ templates, onCreateBlank, onOpenTemplate, onCreateTemplate, onTemplateDeleted }) => {
  const [showAll, setShowAll] = useState(false);
  const [visibleCount, setVisibleCount] = useState(6);
  const [selectedCategory, setSelectedCategory] = useState(['All']); // support multiple selections, 'All' means no filter
  const previewRef = useRef(null);
  const { admins = [], userInfo } = MindMapContext();
  const userEmail = userInfo?.email;
  const isAdmin = Array.isArray(admins) && admins.some(a => (a.adminEmail || a.email) === userEmail);

  useEffect(() => {
    const calculateVisibleCount = () => {
      if (previewRef.current) {
        const container = previewRef.current;
        const containerWidth = container.offsetWidth;
        const cardMinWidth = 180;
        const gap = 16;
        const numCards = Math.floor((containerWidth + gap) / (cardMinWidth + gap));
        setVisibleCount(Math.max(1, numCards));
      }
    };

    calculateVisibleCount();
    window.addEventListener('resize', calculateVisibleCount);

    // Also recalculate on zoom (which triggers resize)
    const observer = new ResizeObserver(calculateVisibleCount);
    if (previewRef.current) {
      observer.observe(previewRef.current);
    }

    return () => {
      window.removeEventListener('resize', calculateVisibleCount);
      observer.disconnect();
    };
  }, []);

  // filter templates based on selected category(s)
  const filteredTemplates = templates.filter(t => {
    if (selectedCategory.includes('All')) return true;
    const cats = t.category || [];
    if (Array.isArray(cats)) {
      return selectedCategory.some(sel => cats.includes(sel));
    }
    return selectedCategory.includes(cats);
  });

  // whenever selection changes collapse preview (don't stay expanded)
  useEffect(() => {
    setShowAll(false);
  }, [selectedCategory]);

  return (
    <div className="tg-wrapper">
      <div className={`tg-inner ${showAll ? 'tg-inner--expanded' : 'tg-inner--preview'}`}>
        <div className="tg-header">
          <h4 className="tg-title">Start a new document</h4>
          <div className="tg-header-right">
            {filteredTemplates.length > 0 && (
              <div
                className={`tg-controls ${filteredTemplates.length <= visibleCount ? 'tg-controls--disabled' : ''}`}
                role="button"
                aria-expanded={showAll}
                onClick={filteredTemplates.length > visibleCount ? () => setShowAll((s) => !s) : undefined}
                tabIndex={filteredTemplates.length > visibleCount ? 0 : -1}
                onKeyPress={filteredTemplates.length > visibleCount ? (e) => { if (e.key === 'Enter' || e.key === ' ') setShowAll(s => !s); } : undefined}
              >
                <span className="tg-controls-text">View Template Gallery</span>
                <span className={`tg-controls-caret ${showAll ? 'open' : ''}`}>▾</span>
              </div>
            )}
            {isAdmin && (
              <button className="tg-create-template-btn" onClick={onCreateTemplate}>
                + Create Template
              </button>
            )}
          </div>
        </div>

        {/* category pills row after header title */}
        <div className="tg-categories-row">
          {['All', ...TEMPLATE_CATEGORIES].map(cat => {
            const isSelected = selectedCategory.includes(cat);
            return (
              <span
                key={cat}
                className={`tg-category-pill ${isSelected ? 'selected' : ''}`}
                onClick={() => {
                  if (cat === 'All') {
                    setSelectedCategory(['All']);
                  } else {
                    setSelectedCategory(prev => {
                      // if All was selected, clear it first
                      let next = prev.filter(x => x !== 'All');
                      if (next.includes(cat)) {
                        next = next.filter(x => x !== cat);
                      } else {
                        next.push(cat);
                      }
                      // if nothing remains, default back to All
                      if (next.length === 0) return ['All'];
                      return next;
                    });
                  }
                }}
              >
                {cat}
              </span>
            );
          })}
        </div>

        {filteredTemplates.length === 0 ? (
          <div className="tg-no-templates">No templates available.</div>
        ) : (
          <>
            {!showAll && (
              <div ref={previewRef} className={`tg-list tg-list--preview`}>
                {filteredTemplates.slice(0, visibleCount).map((template) => {
                  return <TemplateCard key={template._id} template={template} onOpenTemplate={onOpenTemplate} onDeleted={onTemplateDeleted} />;
                })}
              </div>
            )}
            {/* Expanded grid: appears below header as a full-width section */}
            {showAll && (
              <div className="tg-expanded">
                <div className="tg-list tg-list--expanded">
                  {filteredTemplates.map((template) => {
                    return <TemplateCard key={template._id} template={template} onOpenTemplate={onOpenTemplate} onDeleted={onTemplateDeleted} />;
                  })}
                </div>
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
};

export default TemplateGallery;
